import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@osd/ui/button';
import { SpinnerButton } from '@osd/ui/spinner-button';
import { Stack } from '@osd/ui/stack';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/button — live docs for the `Button` primitive.
 */
export const Route = createFileRoute('/ui/_layout/button')({
  component: ButtonPage,
});

function ButtonPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Button"
        hint="default | primary | danger | ghost — variants via data-attribute"
      >
        <Stack gap={3}>
          <Button>Default</Button>
          <Button variant="primary">Primary</Button>
          <Button variant="danger">Delete</Button>
          <Button variant="ghost">Cancel</Button>
          <Button disabled>Disabled</Button>
        </Stack>
      </Section>

      <Section
        title="SpinnerButton"
        hint="Button with a built-in loading affordance (aria-busy, disabled, accessible label override)"
      >
        <Stack gap={3}>
          <SpinnerButton loading loadingLabel="Saving your changes">
            Save changes
          </SpinnerButton>
          <SpinnerButton variant="primary" loading>
            Submitting…
          </SpinnerButton>
          <SpinnerButton variant="danger" loading={false}>
            Delete account
          </SpinnerButton>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Variant — primary action"
            language="tsx"
            code={`<Button variant="primary" type="submit">Save changes</Button>`}
          />

          <CodeBlock
            title="Destructive action"
            hint='Use variant="danger" for irreversible actions; pair with a confirm dialog'
            language="tsx"
            code={`<Button variant="danger" onClick={onDelete}>Delete account</Button>`}
          />

          <CodeBlock
            title="Disabled state"
            language="tsx"
            code={`<Button disabled={form.submitting}>Submit</Button>`}
          />

          <CodeBlock
            title="SpinnerButton — async submit"
            language="tsx"
            code={`const [busy, setBusy] = useState(false);

<SpinnerButton
  variant="primary"
  loading={busy}
  loadingLabel="Saving your changes"
  onClick={async () => {
    setBusy(true);
    try { await save(); } finally { setBusy(false); }
  }}
>
  Save changes
</SpinnerButton>`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
